import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { triviaQuestions } from "../constants/triviaQuestions";
import { getTodayDay } from "../utils/currentDate";

const MILESTONES = [5, 10, 15, 20, 25, 30];

export function DailyQuestion({ userId, onClose, onAnswered }: { userId: string, onClose: () => void, onAnswered: () => void }) {
  const [progress, setProgress] = useState<any>(null);
  const [showQuestion, setShowQuestion] = useState(false);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [hoveredOption, setHoveredOption] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<null | boolean>(null);
  const [showDone, setShowDone] = useState(false);
  const [submitHover, setSubmitHover] = useState(false); // <-- Add this
  const todayDay = getTodayDay();
  const todayKey = String(todayDay);
  const question = triviaQuestions.find(q => q.day === todayDay);

  useEffect(() => {
    async function fetchProgress() {
      const docSnap = await getDoc(doc(db, "users", userId));
      if (docSnap.exists()) {
        const triviaAnswers = docSnap.data().triviaAnswers || {};
        // Use triviaAnswers for your logic
        const alreadyAnswered = triviaAnswers && triviaAnswers[todayKey]?.answered;
        if (!alreadyAnswered) {
          setShowQuestion(true);
        }
      }
    }
    fetchProgress();
  }, [userId, todayKey]);

  const handleAnswer = async (userAnswer: string | number) => {
    let isCorrect = false;
    if (question?.type === "multiple-choice") {
      isCorrect = userAnswer === question.correctIndex;
    } else if (question?.type === "text-input") {
      isCorrect = userAnswer.toString().trim().toLowerCase() === question.answerText?.trim().toLowerCase();
    }
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    const triviaAnswers = userDoc.exists() ? userDoc.data().triviaAnswers || {} : {};
    const alreadyAnswered = triviaAnswers && triviaAnswers[todayKey]?.answered;

    // Calculate new totalCorrect
    let totalCorrect = userDoc.exists() ? userDoc.data().totalCorrect || 0 : 0;
    let milestones = userDoc.exists() ? userDoc.data().milestones || [] : [];

    // Only increment if this is the first time answering correctly today
    if (!alreadyAnswered && isCorrect) {
      totalCorrect += 1;
    }

    // Check for new milestone
    let newMilestone = null;
    if (!alreadyAnswered && isCorrect) {
      for (const m of MILESTONES) {
        if (totalCorrect === m && !milestones.includes(m)) {
          milestones = [...milestones, m];
          newMilestone = m;
          break;
        }
      }
    }

    const updatedAnswers = {
      ...triviaAnswers,
      [todayKey]: {
        answered: true,
        correct: isCorrect,
        answer: userAnswer,
        timestamp: new Date().toISOString()
      },
    };
    await updateDoc(userRef, {
      triviaAnswers: updatedAnswers,
      totalCorrect,
      milestones,
    });
    setFeedback(isCorrect);
    setShowDone(true);

    // Optionally, show a special tooltip or modal if newMilestone is set
  };

  if (!showQuestion) return null;

  if (!question) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(36, 62, 99, 0.45)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Inter, Roboto, system-ui, sans-serif",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "18px",
            boxShadow: "0 8px 32px rgba(36,62,99,0.18)",
            padding: "40px 28px",
            minWidth: "340px",
            maxWidth: "480px",
            textAlign: "center",
          }}
        >
          <h2>No trivia question for today.</h2>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(36, 62, 99, 0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: '"Quicksand", "Inter", sans-serif',
      }}
    >
      <div
        style={{
          position: "relative",
          background: "#f9fafb",
          borderRadius: "18px",
          boxShadow: "0 8px 32px rgba(36,62,99,0.18)",
          padding: "40px 28px",
          minWidth: "340px",
          maxWidth: "480px",
          width: "95vw",
          textAlign: "center",
          border: "1px solid #e2e8f0",
          fontFamily: "inherit",
        }}
      >
        {/* Tooltip-style feedback */}
        {feedback !== null && (
          <div
            style={{
              position: "absolute",
              top: "-80px",
              left: "50%",
              transform: "translateX(-50%)",
              background: feedback ? "#4caf50" : "#ff4d4d",
              color: "#fff",
              fontFamily: "'Quicksand', sans-serif",
              fontStyle: "italic",
              fontWeight: 700,
              fontSize: "1.22rem",
              padding: "16px 38px",
              borderRadius: "16px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.14)",
              opacity: 1,
              zIndex: 2001,
              transition: "opacity 0.3s",
              pointerEvents: "none",
              animation: "fadeInOut 1.5s"
            }}
          >
            {feedback ? "Correct!" : "Incorrect"}
          </div>
        )}
        <h2 style={{
          marginBottom: "28px",
          color: "#234e52",
          fontWeight: 700,
          fontSize: "1.35rem",
          letterSpacing: "0.01em",
          fontFamily: "inherit"
        }}>
          {question.question}
        </h2>
        {question.type === "multiple-choice" ? (
          <div>
            {question.choices?.map((choice, idx) => {
              const highlightCorrect =
                feedback === false && idx === question.correctIndex;
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedOption(idx)}
                  onMouseEnter={() => setHoveredOption(idx)}
                  onMouseLeave={() => setHoveredOption(null)}
                  disabled={feedback !== null}
                  style={{
                    backgroundColor:
                      highlightCorrect
                        ? "#d1fae5"
                        : selectedOption === idx
                          ? "#319795"
                          : hoveredOption === idx
                            ? "#b2f5ea"
                            : "#e6fffa",
                    color:
                      highlightCorrect
                        ? "#234e52"
                        : selectedOption === idx
                          ? "#fff"
                          : "#234e52",
                    border:
                      highlightCorrect
                        ? "2px solid #38a169"
                        : selectedOption === idx
                          ? "2px solid #234e52"
                          : hoveredOption === idx
                            ? "2px solid #319795"
                            : "1px solid #b2f5ea",
                    margin: "7px 0",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    cursor: feedback !== null ? "not-allowed" : "pointer",
                    fontSize: "1rem",
                    fontWeight: selectedOption === idx || highlightCorrect ? 700 : 500,
                    width: "100%",
                    transition: "background 0.2s, border 0.2s",
                    fontFamily: "inherit"
                  }}
                >
                  {choice}
                </button>
              );
            })}
            <button
              onClick={() => {
                if (showDone) {
                  setShowQuestion(false);
                  setFeedback(null);
                  setShowDone(false);
                  onClose();
                  onAnswered();
                } else if (selectedOption !== null) {
                  handleAnswer(selectedOption);
                }
              }}
              disabled={selectedOption === null && !showDone || feedback !== null && !showDone}
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              style={{
                backgroundColor: showDone
                  ? "#7eaaff"
                  : selectedOption !== null && feedback === null
                    ? submitHover
                      ? "#48bb78" // darker green on hover
                      : "#38d39f" // <-- light green when enabled and not hovered
                    : "#e2e8f0",
                color: showDone
                  ? "#24406b"
                  : selectedOption !== null && feedback === null
                    ? "#fff"
                    : "#888",
                border: "none",
                marginTop: "22px",
                padding: "14px 36px",
                borderRadius: "10px",
                cursor:
                  (selectedOption !== null && feedback === null) || showDone
                    ? "pointer"
                    : "not-allowed",
                fontWeight: 700,
                fontSize: "1.1rem",
                boxShadow:
                  ((selectedOption !== null && feedback === null) || showDone)
                    ? "0 2px 8px rgba(56,161,105,0.12)"
                    : "none",
                transition:
                  "background 0.25s cubic-bezier(.4,0,.2,1), box-shadow 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1)",
                fontFamily: "inherit",
                transform: submitHover
                  ? "scale(1.05)"
                  : "scale(1)",
                background:
                  showDone && submitHover
                    ? "#a7d8ff"
                    : showDone
                      ? "#7eaaff"
                      : undefined
              }}
            >
              {showDone ? "Done" : "Submit"}
            </button>
          </div>
        ) : (
          <form
            onSubmit={e => {
              e.preventDefault();
              if (showDone) {
                setShowQuestion(false);
                setFeedback(null);
                setShowDone(false);
                onClose();
                onAnswered();
              } else {
                const answer = (e.target as any).elements.answer.value;
                handleAnswer(answer);
              }
            }}
            style={{ marginTop: "18px" }}
          >
            <input
              name="answer"
              type="text"
              disabled={feedback !== null}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #b2f5ea",
                fontSize: "1rem",
                marginBottom: "16px",
                width: "85%",
                background: "#e6fffa",
                color: "#234e52",
                fontFamily: "inherit"
              }}
            />
            <br />
            <button
              type={showDone ? "button" : "submit"}
              disabled={feedback !== null && !showDone}
              onClick={e => {
                if (showDone) {
                  setShowQuestion(false);
                  setFeedback(null);
                  setShowDone(false);
                  onClose();
                  onAnswered();
                }
              }}
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              style={{
                backgroundColor: showDone
                  ? "#7eaaff"
                  : feedback === null
                    ? submitHover
                      ? "#48bb78"
                      : "#38d39f" // <-- light green when enabled and not hovered
                    : "#e2e8f0",
                color: showDone
                  ? "#24406b"
                  : feedback === null
                    ? "#fff"
                    : "#888",
                border: "none",
                padding: "12px 32px",
                borderRadius: "10px",
                cursor: feedback === null || showDone ? "pointer" : "not-allowed",
                fontWeight: 700,
                fontSize: "1.1rem",
                boxShadow: feedback === null || showDone ? "0 2px 8px rgba(56,161,105,0.12)" : "none",
                transition:
                  "background 0.25s cubic-bezier(.4,0,.2,1), box-shadow 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1)",
                fontFamily: "inherit",
                transform: submitHover
                  ? "scale(1.05)"
                  : "scale(1)",
                background:
                  showDone && submitHover
                    ? "#a7d8ff"
                    : showDone
                      ? "#7eaaff"
                      : undefined
              }}
            >
              {showDone ? "Done" : "Submit"}
            </button>
          </form>
        )}
        {/* Show Done button after feedback */}
      </div>
    </div>
  );
}